import { Grid2 as Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DefaultInputPropTypes, useInputHelper } from 'framework/form';
import { useNewProjectService } from 'domain/shared/newProject';
import { useEmployeeService } from 'domain/shared/employee';
import { GUIDELINE_1, GUIDELINE_2, GUIDELINE_3, GUIDELINE_4 } from './templateConstants';
import {
  fetchTemplate,
  MarkdownEditor,
  markdownToHtml,
  PlaceholderValues,
  replacePlaceholders,
  TemplatePreview,
} from 'framework/markdownEditor';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';

const MDEditorWithPreview = ({
  projectId,
  defaultPlaceholders,
  ...props
}: DefaultInputPropTypes<string> & {
  projectId: string | undefined;
  defaultPlaceholders?: PlaceholderValues;
}) => {
  const inputHelper = useInputHelper(props);
  const onInputChange = inputHelper.getOnChangeCallback();

  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [placeholderValues, setPlaceholderValues] = useState<PlaceholderValues>({
    guideline1: GUIDELINE_1,
    guideline2: GUIDELINE_2,
    guideline3: GUIDELINE_3,
    guideline4: GUIDELINE_4,
  });

  const { addMessage } = useMessageService();
  const { getById, getAccountRealName } = useNewProjectService();
  const { getEmployeeName } = useEmployeeService();
  const { getUserAlias } = useAuthenticatedUserService();

  const getInitValueName = async (alias: string | undefined) => {
    if (!alias) return undefined;
    return getEmployeeName(alias).then(response => `${response.firstName} ${response.lastName}`);
  };

  const calculatePreviewHtml = useCallback(
    (content: string) => {
      if (content) {
        try {
          // replace guideline1 with <span style="color:grey">{{ guideline1 }}</span>
          const replacedContent = content.replace(
            /\{\{\sguideline(\d+)\s\}\}/g,
            '<span style="color:grey">{{ guideline$1 }}</span>',
          );
          const contentWithValues = replacePlaceholders(replacedContent, placeholderValues);

          const html = markdownToHtml(contentWithValues);
          setPreviewHtml(html);
        } catch (error) {
          inputHelper.setFormFieldErrors(['Incorrect Markdown content']);
          addMessage(new ErrorMessage('Error during rendering Markdown to HTML'));
        }
      }
    },
    [addMessage, inputHelper, placeholderValues],
  );

  const handleContentChange = useCallback(
    (content: string) => {
      onInputChange({ target: { value: content } });
      calculatePreviewHtml(content);
    },
    [calculatePreviewHtml, onInputChange],
  );

  useEffect(() => {
    if (defaultPlaceholders) {
      setPlaceholderValues(prev => ({ ...prev, ...defaultPlaceholders }));
    }
  }, [defaultPlaceholders]);

  useEffect(() => {
    const loadProjectData = async () => {
      try {
        const projectData = projectId ? await getById(projectId) : {};
        const currentUserAlias = await getUserAlias();
        const sensitiveAccountName = await getAccountRealName(projectId!);
        const currentUser = currentUserAlias ? await getInitValueName(currentUserAlias) : EMPTY_VALUE_INDICATOR;
        const projectPlaceholders: PlaceholderValues = {
          accountName: sensitiveAccountName?.originalName || projectData.accountName,
          projectName: projectData.name,
          projectManager: currentUser,
        };
        setPlaceholderValues(prev => ({ ...prev, ...projectPlaceholders }));
      } catch (err) {
        addMessage(new ErrorMessage(`Error loading data: ${err instanceof Error ? err.message : String(err)}`));
      }
    };

    loadProjectData();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadTemplateData = async () => {
      try {
        const initialContent = await fetchTemplate({ templateFileName: 'cr-template.md' }).then(
          result => result?.content,
        );
        onInputChange({ target: { value: initialContent } });
      } catch (err) {
        addMessage(new ErrorMessage(`Error loading data: ${err instanceof Error ? err.message : String(err)}`));
      }
    };

    if (!inputHelper.getValue()) {
      loadTemplateData();
    }
    calculatePreviewHtml(inputHelper.getValue());
  }, [calculatePreviewHtml, inputHelper]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid container spacing={1} pb={4}>
      <Grid size={6} sx={{ border: '1px solid', borderColor: 'border.default', borderRadius: 1 }}>
        <MarkdownEditor
          value={inputHelper.getValue()}
          onChange={handleContentChange}
          editorSx={{ height: '600px' }}
          containerSx={{ height: '600px' }}
        />
      </Grid>
      <Grid size={6} sx={{ border: '1px solid', borderColor: 'border.default', borderRadius: 1 }}>
        <TemplatePreview
          paperSx={{ '& h2, & h1, & h3, & h4': { margin: '20px 0 10px 0' }, p: 2 }}
          html={previewHtml}
          containerSx={{ height: '600px' }}
        />
      </Grid>
    </Grid>
  );
};

export default MDEditorWithPreview;
